
import { supabase } from '@/lib/supabase';
import { handleError, ErrorSeverity, ErrorContext } from '@/utils/errorHandler';
import { addDays } from 'date-fns';

export interface SubscriptionData {
  id: string;
  organization_id: string;
  subscription_price_id: string;
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'unpaid';
  current_period_start: string;
  current_period_end: string;
  trial_start?: string;
  trial_end?: string;
  cancel_at?: string;
  canceled_at?: string;
}

export const SubscriptionService = {
  /**
   * Get current subscription for an organization
   */
  async getCurrentSubscription(organizationId: string): Promise<SubscriptionData | null> {
    const context: ErrorContext = {
      operation: 'getCurrentSubscription',
      additionalInfo: { organizationId }
    };

    try {
      // First try to get a real subscription from the database
      const { data: subscription, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('organization_id', organizationId)
        .in('status', ['trialing', 'active', 'past_due'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching subscription:', error);
        // Don't throw - just return null for missing subscriptions
      }

      if (subscription) {
        return subscription as SubscriptionData;
      }
      
      // No subscription found
      return null;
    } catch (error) {
      handleError(error, "Failed to get current subscription", ErrorSeverity.ERROR, context);
      return null;
    }
  },

  /**
   * Create a Stripe checkout session for the subscription
   */
  async createCheckoutSession(
    organizationId: string,
    priceSlug: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<{ url: string } | null> {
    const context: ErrorContext = {
      operation: 'createCheckoutSession',
      additionalInfo: { organizationId, priceSlug }
    };

    try {
      console.log(`Creating checkout session for org ${organizationId} with price ${priceSlug}`);
      
      const { data, error } = await supabase.functions.invoke('create-subscription', {
        body: {
          organizationId,
          priceSlug,
          successUrl,
          cancelUrl
        }
      });

      if (error) {
        console.error('Error invoking create-subscription:', error);
        throw error;
      }
      
      if (!data?.url) {
        console.error('No URL returned from create-subscription:', data);
        throw new Error(data?.error || 'Failed to create checkout session');
      }

      return data as { url: string };
    } catch (error) {
      handleError(error, "Failed to create checkout session", ErrorSeverity.ERROR, context);
      return null;
    }
  },

  /**
   * Create a Stripe customer portal session
   */
  async createPortalSession(
    organizationId: string,
    returnUrl: string
  ): Promise<{ url: string } | null> {
    const context: ErrorContext = {
      operation: 'createPortalSession',
      additionalInfo: { organizationId }
    };

    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: {
          organizationId,
          returnUrl
        }
      });

      if (error) throw error;
      return data as { url: string };
    } catch (error) {
      handleError(error, "Failed to create portal session", ErrorSeverity.ERROR, context);
      return null;
    }
  },

  /**
   * Initialize trial for a new organization
   */
  async initializeTrial(organizationId: string, trialDays = 14): Promise<boolean> {
    const context: ErrorContext = {
      operation: 'initializeTrial',
      additionalInfo: { organizationId, trialDays }
    };

    try {
      const { error } = await supabase.functions.invoke('initialize-trial', {
        body: {
          organizationId,
          trialDays
        }
      });

      if (error) throw error;
      return true;
    } catch (error) {
      handleError(error, "Failed to initialize trial", ErrorSeverity.ERROR, context);
      return false;
    }
  },
  
  /**
   * Get organization trial status
   */
  async getOrganizationTrialStatus(organizationId: string): Promise<{
    isInTrial: boolean,
    trialDaysRemaining: number,
    trialEndDate: string | null,
    subscriptionStatus: string | null,
    trialEndsAt: string | null
  }> {
    const context: ErrorContext = {
      operation: 'getOrganizationTrialStatus',
      additionalInfo: { organizationId }
    };

    try {
      // First, get the organization data to check trial_ends_at
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('trial_ends_at, subscription_status, custom_trial_days, created_at')
        .eq('id', organizationId)
        .single();

      if (orgError || !orgData) {
        throw new Error('Failed to fetch organization data');
      }

      // Then check for active subscription
      const subscription = await this.getCurrentSubscription(organizationId);
      
      // If there's an active (non-trial) subscription, not in trial
      if (subscription && subscription.status === 'active') {
        return {
          isInTrial: false,
          trialDaysRemaining: 0,
          trialEndDate: null,
          subscriptionStatus: 'active',
          trialEndsAt: orgData.trial_ends_at
        };
      }
      
      // Check organization trial_ends_at field
      if (orgData.trial_ends_at) {
        const trialEnd = new Date(orgData.trial_ends_at);
        const today = new Date();
        const trialDaysRemaining = Math.max(0, Math.ceil((trialEnd.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
        
        return {
          isInTrial: trialDaysRemaining > 0 && orgData.subscription_status !== 'active',
          trialDaysRemaining,
          trialEndDate: orgData.trial_ends_at,
          subscriptionStatus: orgData.subscription_status || 'trialing',
          trialEndsAt: orgData.trial_ends_at
        };
      }
      
      // If no trial_ends_at is set, calculate based on creation date
      // This handles new organizations that haven't had their trial initialized
      const defaultTrialDays = orgData.custom_trial_days || 14;
      
      // Calculate trial days remaining based on organization creation date
      if (orgData.created_at) {
        const createdAt = new Date(orgData.created_at);
        const trialEndDate = new Date(createdAt);
        trialEndDate.setDate(trialEndDate.getDate() + defaultTrialDays);
        
        const today = new Date();
        const trialDaysRemaining = Math.max(0, Math.ceil((trialEndDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
        
        return {
          isInTrial: trialDaysRemaining > 0,
          trialDaysRemaining,
          trialEndDate: trialEndDate.toISOString(),
          subscriptionStatus: 'trialing',
          trialEndsAt: null
        };
      }
      
      // Fallback if no created_at date (shouldn't happen in practice)
      return {
        isInTrial: true,
        trialDaysRemaining: defaultTrialDays,
        trialEndDate: null,
        subscriptionStatus: 'trialing',
        trialEndsAt: null
      };
      
    } catch (error) {
      handleError(error, "Failed to get trial status", ErrorSeverity.ERROR, context);
      return {
        isInTrial: false,
        trialDaysRemaining: 0,
        trialEndDate: null,
        subscriptionStatus: null,
        trialEndsAt: null
      };
    }
  },

  /**
   * Resume an abandoned checkout session for an organization
   */
  async resumeCheckout(organizationId: string): Promise<{
    success: boolean;
    redirect_url?: string;
    message: string;
    should_create_new?: boolean;
  }> {
    const context: ErrorContext = {
      operation: 'resumeCheckout',
      additionalInfo: { organizationId }
    };

    try {
      console.log(`Attempting to resume checkout for organization ${organizationId}`);
      
      const { data, error } = await supabase.functions.invoke('resume-checkout', {
        body: {
          organization_id: organizationId
        }
      });

      if (error) {
        console.error('Error invoking resume-checkout:', error);
        throw error;
      }

      return data;
    } catch (error) {
      handleError(error, "Failed to resume checkout", ErrorSeverity.ERROR, context);
      return {
        success: false,
        message: "Failed to resume checkout session",
        should_create_new: true
      };
    }
  },

  /**
   * Create an authenticated upgrade checkout session for existing users
   */
  async createUpgradeCheckoutSession(
    organizationId: string,
    priceId: string,
    customerId?: string,
    successUrl?: string,
    cancelUrl?: string
  ): Promise<{ url: string; sessionId: string } | null> {
    const context: ErrorContext = {
      operation: 'createUpgradeCheckoutSession',
      additionalInfo: { organizationId, priceId }
    };

    try {
      console.log(`Creating upgrade checkout session for org ${organizationId} with price ${priceId}`);
      
      const { data, error } = await supabase.functions.invoke('upgrade-checkout', {
        body: {
          priceId,
          customerId,
          successUrl: successUrl || `${window.location.origin}/admin?upgrade=success`,
          cancelUrl: cancelUrl || `${window.location.origin}/admin`,
          organizationId,
          metadata: {
            source: 'authenticated_upgrade'
          }
        }
      });

      if (error) {
        console.error('Error invoking upgrade-checkout:', error);
        throw error;
      }
      
      if (!data?.url) {
        console.error('No URL returned from upgrade-checkout:', data);
        throw new Error(data?.error || 'Failed to create upgrade checkout session');
      }

      return data as { url: string; sessionId: string };
    } catch (error) {
      handleError(error, "Failed to create upgrade checkout session", ErrorSeverity.ERROR, context);
      return null;
    }
  },

  /**
   * Get organization subscription status for access control
   */
  async getOrganizationAccess(organizationId: string): Promise<{
    hasAccess: boolean;
    status: string;
    plan: string;
    trialEndsAt: string | null;
    daysRemaining: number;
    shouldRedirectToCheckout: boolean;
  }> {
    const context: ErrorContext = {
      operation: 'getOrganizationAccess',
      additionalInfo: { organizationId }
    };

    try {
      const { data: orgData, error } = await supabase
        .from('organizations')
        .select('subscription_status, subscription_plan, trial_ends_at')
        .eq('id', organizationId)
        .single();

      if (error || !orgData) {
        throw new Error('Failed to fetch organization data');
      }

      const status = orgData.subscription_status || 'no_subscription';
      const plan = orgData.subscription_plan || 'starter';
      const trialEndsAt = orgData.trial_ends_at;

      // Calculate days remaining for trials
      let daysRemaining = 0;
      if (trialEndsAt && status === 'trial_active') {
        const trialEnd = new Date(trialEndsAt);
        const today = new Date();
        daysRemaining = Math.max(0, Math.ceil((trialEnd.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
      }

      // Determine access and redirect logic
      const hasAccess = ['trial_active', 'paid_active', 'trialing', 'active', 'active-hiveshare'].includes(status);
      // HiveShare users should never be redirected to checkout
      const shouldRedirectToCheckout = status !== 'active-hiveshare' && ['no_subscription', 'trial_pending', 'expired'].includes(status);

      return {
        hasAccess,
        status,
        plan,
        trialEndsAt,
        daysRemaining,
        shouldRedirectToCheckout
      };
    } catch (error) {
      handleError(error, "Failed to get organization access", ErrorSeverity.ERROR, context);
      return {
        hasAccess: false,
        status: 'unknown',
        plan: 'starter',
        trialEndsAt: null,
        daysRemaining: 0,
        shouldRedirectToCheckout: true
      };
    }
  }
};
